// Common form input fields used across the app, built with Formik.
import React, { useEffect, useRef } from "react";
import Select, { components } from "react-select";
import { IMAGE_URL, WS_URL, downloadURL } from "../../utils/Constants";
import { TrimText } from "../common/TrimText";
import { Link } from "react-router-dom";
import { tokens } from "../../public/tokens.js";

/**
 * Generic form field component used for text, number, color, and select inputs.
 *
 * @param {Object} props
 * @param {Object} props.field - Field metadata and current value.
 * @param {string} props.field.name - Field name.
 * @param {string} props.field.label - Field label text.
 * @param {string|number} [props.field.value] - Current field value.
 * @param {boolean} [props.field.isMulti=false] - Whether select input allows multiple values.
 * @param {string} [props.field.type="text"] - Input type, e.g. text, number, color.
 * @param {boolean} [props.field.info=false] - Whether to render an info icon.
 * @param {boolean} [props.field.sign=false] - Whether to render a currency sign badge.
 * @param {boolean} [props.field.color=false] - Whether to render a color preview selector.
 * @param {Object} props.formik - Formik instance used for state and validation.
 * @param {Array} [props.selectOptions] - Optional select options when rendering a dropdown.
 * @param {boolean} [props.required=true] - Whether the field is required.
 * @param {number} [props.col_md=6] - Column width at medium breakpoints.
 * @param {boolean} [props.mbTrue=true] - Whether bottom margin is enabled.
 * @param {number} [props.col_lg=3] - Column width at large breakpoints.
 * @param {string} [props.placeholder] - Input placeholder text.
 * @param {boolean} [props.isClearable=true] - Whether the select value can be cleared.
 * @param {string} [props.inputTitle] - Title attribute for the field container.
 * @param {Function} [props.callback] - Optional callback invoked after select changes.
 * @returns {JSX.Element} Rendered form field.
 */
// Text / Number input component
export const FormField = ({
  field,
  formik,
  selectOptions,
  required = true,
  col_md = 6,
  mbTrue=true,
  col_lg = 3,
  placeholder,
  isClearable=true,
  inputTitle,
  callback
}) => {
  const {
    name,
    label,
    max,
    min,
    title,
    step,
    value,
    isMulti = false,
    type = "text",
    info = false,
    sign = false,
    requiredField = true,
    isInvalid = formik?.touched[name] && formik?.errors[name],
  } = field;
  // const isInvalid = formik.touched[name] && formik.errors[name];
  const colorRef = useRef();
  
  return (
    <div className={`col-md-${col_md} col-lg-${col_lg}`} title={inputTitle ? inputTitle : label}>
      {required ? (
        <label htmlFor={name}>
          {label} {requiredField ? '*' : null } {info ? <i className="fal fa-info-circle"></i> : null}
        </label>
      ) : (
        <label htmlFor={name}>
          {label} {info ? <i className="fal fa-info-circle"></i> : null}
        </label>
      )}
      <div
        className={`form-icon-group mb-4 ${
          selectOptions ? "position-relative" : ""
        }`}
        title={label}
      >
        {selectOptions ? (
          <Select
            className={`form-control custom-select-box ${
              isInvalid && "is-invalid"
            }`}
            name={name}
            value={
              value
                ? selectOptions.find((val) => val.value == value) ?? "" 
                : selectOptions.find((val) => val.value === formik.values[name]) ?? ""
            }
            onChange={(selectedOption) => {
              formik.setFieldValue(name, selectedOption?.value ?? "")
              if(callback){
                callback()
              }
            }
            }
            isClearable={isClearable}
            onBlur={formik.handleBlur}
            options={selectOptions}
            maxMenuHeight={175}
            placeholder="Select"
            isMulti={isMulti}
          />
        ) : (
          <>
            {sign ? (
              <span
                style={{
                  position: "absolute",
                  marginLeft: "5px",
                  marginTop: "13px",
                  fontWeight: "bold",
                }}
              >
                R
              </span>
            ) : null}
            {type == 'number' ?
            <input
              type={type}
              className={`form-control ${isInvalid && "is-invalid"}`}
              name={name}
              // maxLength={type == "color" ? "7" : ""}
              id={name}
              step={step || 1}
              title={label}
              placeholder={placeholder}
              value={value ? value : formik.values[name]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              min={min || -Infinity}
              max={max || Infinity}
            /> 
            :
              <input
              type={type == "color" ? "text" : type}
              className={`form-control ${isInvalid && "is-invalid"}`}
              name={name}
              // maxLength={type == "color" ? "7" : ""}
              id={name}
              title={label}
              placeholder={placeholder}
              value={value ? value : formik.values[name] || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            }
            
            {type == "color" ? (
              <>
                <input
                  className={`color-selector-brand ${isInvalid && "is-invalid"}`}
                  type="color"
                  ref={colorRef}
                  name={name}
                  id={name}
                  maxLength={6}
                  style={{ width: "10px" }}
                  value={formik.values[name]}
                  onChange={(e) => {
                    formik.setFieldValue(name, e.target.value);
                  }}
                  onBlur={formik.handleBlur}
                />
              </>
            ) : null}
          </>
        )}
        {field.color && (
          <div
            className="position-absolute rounded"
            style={{
              backgroundColor: formik.values[name],
              height: "32px",
              width: "32px",
              top: "5px",
              right: "15px",
              cursor:"pointer"
            }}
            onClick={() => colorRef.current.click()}
          ></div>
        )}
      </div>
    </div>
  );
};

/**
 * File upload input component used by Formik forms.
 *
 * @param {Object} props
 * @param {string} props.name - Field name.
 * @param {string} props.label - Display label for the upload field.
 * @param {Object} props.formik - Formik form state instance.
 * @param {string} [props.title] - Tooltip text for the field.
 * @param {string} [props.accept] - Accepted file MIME types.
 * @param {boolean} [props.info=true] - Whether to show the info icon.
 * @param {boolean} [props.required=false] - Whether the field is required.
 * @param {number} [props.col_md=6] - Column width for medium screen sizes.
 * @param {boolean} [props.mbTrue=true] - Whether to apply bottom margin.
 * @param {number} [props.col_lg=3] - Column width for large screen sizes.
 * @returns {JSX.Element}
 */
// File upload component
export const FileUploadField = ({
  name,
  label,
  formik,
  title,
  accept,
  info = true,
  required = false,
  col_md = 6,
  mbTrue=true,
  col_lg = 3,
}) => {
  const isInvalid = formik.touched[name] && formik.errors[name];
  const fileValue = formik.values[name];

  const isFileObject = fileValue instanceof File;
  const fileURL = isFileObject ? URL.createObjectURL(fileValue) : fileValue;

  return (
    <div className={`col-md-${col_md} col-lg-${col_lg} mb-1`} title={label}>
      <div className={`form-group-blk ${mbTrue && "mb-3"}`}> 
        {required ? (
          <label htmlFor={name} >
            {label} * {info ? <i title={title} className="fal fa-info-circle"></i> : null}
          </label>
        ) : (
          <label htmlFor={name}>
            {label} {info ? <i title={title} className="fal fa-info-circle"></i> : null}
          </label>
        )}
        <div className="form-group atttach-file m-0" title={title}>
          <label className={`${isInvalid && "file-req is-invalid"}`}>
            <i className="fal fa-upload"></i>
            <span>Upload</span>

            <input
              type="file"
              className="form-control form-control-aatch-file"
              onChange={(e) => {
                const file = e.currentTarget.files[0];
                if (file) {
                  formik.setFieldValue(name, file);
                }
                e.target.value=""
              }}
              // title={title}
              name={name}
              id={name}
              accept={accept}
            />
          </label>
        </div>
        {fileValue && (
          <>
            <div className="frm-group">
              <ul className="list-unstyled attached-file-ul m-0">
                <li
                  title={
                    fileValue.name
                      ? fileValue.name
                      : fileURL?.split("/")?.reverse()[0]
                  }
                >
                  <a
                    href={
                      fileURL.includes("blob")
                        ? fileURL
                        : `${IMAGE_URL}/${encodeURI(fileURL
                            .replaceAll("public/", "")
                            .replaceAll("home/myaie/public_html/", ""))}`
                    }
                    target="_blank"
                  >
                    <i className="fal fa-file"></i>
                    &nbsp;
                    {fileValue.name
                      ? TrimText(fileValue.name, 40)
                      : TrimText(fileURL.split("/").reverse()[0], 40)}
                  </a>
                  <button
                    type="button"
                    className="btn btn-danger rounded-circle btn-dropdown-item"
                    title="Delete"
                    onClick={() => formik.setFieldValue(name, "")}
                  >
                    <i className="fal fa-trash-alt"></i>
                  </button>
                </li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Button field used to assign a page and display the selected page reference.
 *
 * @param {Object} props
 * @param {string} props.name - Form field name.
 * @param {string} props.label - Label displayed above the button.
 * @param {Object} props.formik - Formik instance used for state.
 * @param {string} props.dataTarget - Modal target selector.
 * @param {Object} props.assignedPage - Currently assigned page metadata.
 * @param {Function} props.setAssignedPage - Setter to clear or update the assigned page.
 * @param {boolean} [props.info=true] - Show info icon.
 * @param {boolean} [props.required=false] - Whether the field is required.
 * @param {number} [props.col_md=6] - Column width for medium screens.
 * @param {number} [props.col_lg=3] - Column width for large screens.
 * @param {string} [props.buttonName="Assign Page"] - Label for the action button.
 * @returns {JSX.Element}
 */
// Bad component, move elsewhere
export const AssignPageField = ({
  name,
  label,
  formik,
  dataTarget,
  assignedPage,
  setAssignedPage,
  info = true,
  required = false,
  col_md = 6,
  col_lg = 3,
  buttonName = "Assign Page"
}) => {
  const isInvalid = formik.touched[name] && formik.errors[name];

  if (!assignedPage) {
    assignedPage = {};
  }

  return (
    <div className={`col-md-${col_md} col-lg-${col_lg}`}>
      <div className="d-flex align-items-start flex-column" title={label}>
        {required ? (
          <label htmlFor={name}>
            {label} *{" "}
            {info ? <i className="fal fa-info-circle grade-icon"></i> : null}
          </label>
        ) : (
          <label htmlFor={name}>
            {label}{" "}
            {info ? <i className="fal fa-info-circle grade-icon"></i> : null}
          </label>
        )}
        <button
          className={
            "btn btn-save btn-success" +
            (isInvalid ? " file-req is-invalid" : "")
          }
          type="button"
          title="Assign Page"
          data-toggle="modal"
          data-target={dataTarget}
        >
          <i className="fal fa-plus"></i> {buttonName}
        </button>
        {formik.values[name] &&
        Object.keys(assignedPage).length &&
        assignedPage?.PageTitle || assignedPage?.title ||  assignedPage?.name ? (
          <>
            <div className="frm-group">
              <ul className="list-unstyled attached-file-ul m-0">
                <li>
                  <Link
                    className="textLimit100 as-text-blue pr-3"
                    title={assignedPage?.PageTitle ? assignedPage?.PageTitle : assignedPage?.title}
                    // to={assignedPage?.flag ? `/website_management/pages/staticpages/table/update/${assignedPage.PageID}` : `/program-management-center/programmes/programme/open/${assignedPage.Id}`}
                    to={`${assignedPage.flag == 4 ? `/website_management/pages/categorylandingpages/table/open/${assignedPage.PageID}` : assignedPage.flag == 0 ? `/website_management/pages/staticpages/table/update/${assignedPage.PageID}` : assignedPage.flag == 2 ? `/website_management/pages/courselandingpages/table/open/${assignedPage.PageID}` : assignedPage.flag == 3 ? `/website_management/pages/campuspages/table/update/${assignedPage.PageID}` : assignedPage.flag == 1 ? `/websiteManagement/events/landing_page/page_details/update/${assignedPage.PageID}` :
                    assignedPage.flag == 5 ? `/websiteManagement/pages/programmes_and_short_courses/programme_pages/details/open/${assignedPage.id}` : assignedPage.flag == 6 ? `/websiteManagement/pages/programmes_and_short_courses/short_course_page/details/open/${assignedPage.id}` : `/website_management/pages/staticpages/table/update/${assignedPage.PageID}`}`}
                  >
                      {TrimText(
                        assignedPage?.PageTitle
                          ? assignedPage.PageTitle
                          : assignedPage?.name
                            ? assignedPage.name
                            : assignedPage?.title,
                        35
                      )}                 
                    </Link>
                  <button
                    className="btn btn-danger rounded-circle btn-dropdown-item ml-3"
                    title="Delete"
                    onClick={() => {
                      formik.setFieldValue(name, "");
                      setAssignedPage({});
                    }}
                  >
                    <i className="fal fa-trash-alt"></i>
                  </button>
                </li>
              </ul>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Multi‑select dropdown component using react‑select.
 *
 * - By default (without `getOptionColor`), the component renders with standard AIE grey chips and consistent arrow styling.
 * - When `getOptionColor` is provided, each selected chip gets a coloured background.
 * - Optionally also pass `getOptionTextColor` to control the chip text and X colour when in coloured mode.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name - Field name (used for form handling).
 * @param {string} props.label - Label text.
 * @param {Array} [props.value=[]] - Currently selected option objects.
 * @param {Array} [props.options=[]] - Available options, each with `value`, `label`, and optional `grouping_color` (if colour mode is used).
 * @param {Function} props.onChange - Callback when selection changes, receives new array.
 * @param {Function} [props.onBlur] - Blur event handler.
 * @param {boolean} [props.required=false] - Whether the field is required (shows asterisk).
 * @param {boolean|string} [props.info=false] - If a non‑empty string, shows an info icon with that tooltip. `false` hides icon.
 * @param {string} [props.placeholder="Select"] - Placeholder text.
 * @param {boolean} [props.isClearable=true] - Whether the clear button is shown.
 * @param {boolean} [props.isDisabled=false] - Disables the dropdown.
 * @param {boolean} [props.isLoading=false] - Shows a loading spinner instead of the dropdown.
 * @param {number} [props.col_md=6] - Bootstrap medium column width.
 * @param {number} [props.col_lg=3] - Bootstrap large column width.
 * @param {Function} [props.getOptionColor] - If provided, returns a background colour for a given option (e.g., `(option) => option.grouping_color`). When absent, uses standard grey chips.
 * @param {Function} [props.getOptionTextColor] - If provided, returns a text colour for a given option when custom chip colours are used. Falls back to `#252423`.
 * @param {string} [props.highlightColor] - Optional background colour for focused/selected options when `getOptionColor` is provided. If omitted, the default react‑select highlight is used.
 * @param {boolean} [props.isInvalid=false] - If `true`, adds Bootstrap `is-invalid` class for validation feedback.
 * @returns {JSX.Element}
 */
export const MultiSelectDropdown = ({
  name,
  label,
  value = [],
  options = [],
  onChange,
  onBlur,
  required = false,
  info = false,
  placeholder = "Select",
  isClearable = true,
  isDisabled = false,
  isLoading = false,
  col_md = 6,
  col_lg = 3,
  getOptionColor = null,
  getOptionTextColor = null,
  highlightColor,
  isInvalid = false,
}) => {
  
  const whiteColor = tokens.colors.neutral.white;
  const textPrimary = tokens.colors.text.primary;
  const primaryColor = tokens.colors.ui.primary;

  const hasCustomColor = typeof getOptionColor === "function";
  const hasCustomTextColor = typeof getOptionTextColor === "function";
  const hasCustomHighlight = typeof highlightColor === "string";


  const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
      <i className="fal fa-chevron-down" />
    </components.DropdownIndicator>
  );

  let finalSelectStyles = undefined;
  let customComponents = undefined;

  if (hasCustomColor) {
    finalSelectStyles = {
      control: (base) => ({
        ...base,
        borderRadius: "10px",
        minHeight: "42px",
        boxShadow: "none",
        alignItems: "stretch",
      }),
      dropdownIndicator: (base) => ({
        ...base,
        width: "46px",
        padding: 0,
        color: "#252423",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }),
      indicatorSeparator: (base) => ({
        ...base,
        marginTop: "8px",
        marginBottom: "8px",
      }),
      indicatorsContainer: (base) => ({
        ...base,
        alignSelf: "stretch",
        display: "flex",
        alignItems: "center",
      }),
      multiValue: (base, { data }) => ({
        ...base,
        backgroundColor: getOptionColor(data) || primaryColor,
        borderRadius: "8px",
        margin: "4px",
      }),
      multiValueLabel: (base, { data }) => ({
        ...base,
        color: hasCustomTextColor ? getOptionTextColor(data) : "#252423",
        fontWeight: 600,
      }),
      multiValueRemove: (base, { data }) => {
        const removeColor = hasCustomTextColor ? getOptionTextColor(data) : "#252423";
        return {
          ...base,
          color: removeColor,
          ":hover": {
            backgroundColor: "transparent",
            color: removeColor,
          },
        };
      },
    };

    if (hasCustomHighlight) {
      finalSelectStyles.option = (base, state) => ({
        ...base,
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        backgroundColor: state.isFocused || state.isSelected ? highlightColor : whiteColor,
        color: state.isFocused || state.isSelected ? whiteColor : textPrimary,
        cursor: "pointer",
        ":active": {
          backgroundColor: highlightColor,
        },
      });
    }

    customComponents = {
      DropdownIndicator,
      ClearIndicator: null,
    };
  }

  const formatOptionLabel = hasCustomColor
    ? (option, { context }) => {
        if (context === "value") return option.label;
        return (
          <span
            className="badge badge-pill"
            style={{
              backgroundColor: getOptionColor(option) || primaryColor,
              color: hasCustomTextColor ? getOptionTextColor(option) : "#252423",
              borderRadius: "8px",
              padding: "4px 10px",
              fontWeight: 600,
            }}
          >
            {option.label}
          </span>
        );
      }
    : undefined;

  // Render label with optional asterisk and optional info icon (only when info is a non‑empty string)
  const renderLabel = () => {
    const showIcon = typeof info === "string" && info.trim().length > 0;
    const tooltip = showIcon ? info : "";
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
        <span>{label} {required && "*"}</span>
        {showIcon && (
          <i
            className="fal fa-info-circle"
            style={{ cursor: "help", fontSize: "0.9em" }}
            title={tooltip}
          />
        )}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className={`col-md-${col_md} col-lg-${col_lg}`}>
        <label htmlFor={name}>{renderLabel()}</label>
        <div className="form-icon-group mb-4">
          <div className="form-control d-flex align-items-center" style={{ height: 38 }}>
            <i className="fas fa-spinner fa-spin mr-2"></i>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`col-md-${col_md} col-lg-${col_lg}`}>
      <label
        htmlFor={name}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexWrap: "wrap",
        }}
      >
        {renderLabel()}
      </label>
      <div className="form-icon-group mb-4">
        <Select
          className={`form-control custom-select-box ${isInvalid ? "is-invalid" : ""}`}
          name={name}
          value={value}
          onChange={(selectedOptions) => onChange(selectedOptions || [])}
          onBlur={onBlur}
          options={options}
          maxMenuHeight={175}
          placeholder={placeholder}
          isMulti
          isClearable={isClearable}
          isDisabled={isDisabled}
          styles={finalSelectStyles}
          formatOptionLabel={formatOptionLabel}
          components={customComponents}
        />
      </div>
    </div>
  );
};

/**
 * Single-select dropdown field wrapper around react-select.
 *
 * @param {Object} props
 * @param {Object} props.field - Field metadata and current value.
 * @param {Object} props.formik - Formik state object.
 * @param {Array} props.selectOptions - Select option list.
 * @param {boolean} [props.required=true] - Whether the field is required.
 * @param {number} [props.col_md=6] - Column width for medium breakpoints.
 * @param {boolean} [props.mbTrue=true] - Whether to apply bottom margin.
 * @param {number} [props.col_lg=3] - Column width for large breakpoints.
 * @param {string} [props.placeholder] - Placeholder text for the select input.
 * @param {boolean} [props.isClearable=true] - Whether the select input can be cleared.
 * @param {string} [props.inputTitle] - Title attribute shown on hover.
 * @param {Function} [props.callback] - Optional callback invoked after selection.
 * @returns {JSX.Element}
 */
export const SingleSelectDropdown = ({
  name,
  label,
  title,
  value,
  formik,
  selectOptions,
  required = true,
  info = false,
  sign = false,
  col_md = 6,
  mbTrue = true,
  col_lg = 3,
  placeholder,
  isClearable = true,
  inputTitle,
  isInvalid = formik?.touched[name] && formik?.errors[name],
  callback
}) => {
  // const isInvalid = formik.touched[name] && formik.errors[name];
  const colorRef = useRef();
  return (
    <div className={`col-md-${col_md} col-lg-${col_lg}`} title={inputTitle ? inputTitle : label}>
      {required ? (
        <label htmlFor={name}>
          {label} * {info ? <i className="fal fa-info-circle"></i> : null}
        </label>
      ) : (
        <label htmlFor={name}>
          {label} {info ? <i className="fal fa-info-circle"></i> : null}
        </label>
      )}
      <div
        className={`form-icon-group mb-4 ${selectOptions ? "position-relative" : ""
          }`}
        title={label}
      >
        <Select
          className={`form-control custom-select-box ${isInvalid && "is-invalid"
            }`}
          name={name}
          value={
            value
              ? selectOptions.find((val) => val.value == value) || ""
              : selectOptions.find((val) => val.value == formik?.values[name]) || ""
          }
          onChange={(selectedOption) => {

            formik.setFieldValue(name, selectedOption ? selectedOption.value : "")
            if (callback) {
              callback()
            }
          }
          }
          isClearable={isClearable}
          onBlur={formik.handleBlur}
          options={selectOptions}
          maxMenuHeight={175}
          placeholder="Select"
        />
      </div>
    </div>
  );
};

// Missing: Radio button input, 
// Checkbox Input, 
//  SingleSelect Input,
